import {
    PayrollCalendarState,
    PayrollDatesInYear,
    PayrollCalendarDate,
    DateGenerationCode,
    PayrollValidationMessage
} from "@/employer/types";
import * as PayrollService from "@/shared/api/payrollService";
import moment from "moment";
import { FetchState } from "@/shared/types";
import * as storeHelper from "@/shared/storeHelper";
import { defineStore } from "pinia";
import _ from "lodash";

export const initialState: PayrollCalendarState = {
    byYear: [],
    validationFetchState: FetchState.Uninitialized,
    employerId: null,
    validationMessages: []
};

const descendingOrder = (a: number, b: number) => b - a;

const DATE = "YYYY-MM-DD";

const monthFromDate = (dateString: string) => dateString.slice(5, 7);

const emptyPayrollDatesInYear = (year: number): PayrollDatesInYear => ({
    year,
    payrollDates: [],
    pristinePayrollDates: [],
    fetchState: FetchState.Uninitialized,
    isDirty: false
});

const findYearOrEmpty = (state: PayrollCalendarState, year: number): PayrollDatesInYear => {
    return state.byYear.find(o => o.year === year) || emptyPayrollDatesInYear(year);
};
const getOrAddYearObject = (state: PayrollCalendarState, year: number) => {
    let ret = state.byYear.find(o => o.year === year);
    if (!ret) {
        ret = emptyPayrollDatesInYear(year);
        state.byYear.push(ret);
    }

    return ret;
};
export const usePayrollCalendarStore = defineStore("payrollCalendar", {
    state: (): PayrollCalendarState => (_.cloneDeep(initialState)),
    getters: {
        payrollDatesInYear: state => (year: number) => {
            return findYearOrEmpty(state, year).payrollDates;
        },
        fetchStateForYear: state => (year: number) => {
            return findYearOrEmpty(state, year).fetchState;
        },
        getIsDirty: state => (year: number) => findYearOrEmpty(state, year).isDirty,
        isAnyYearDirty: state => state.byYear.some(yearObj => yearObj.isDirty),
        getYears: state => state.byYear.map(o => o.year),
    },
    actions: {
        setFetchStateForYear (value: { year: number, fetchState: FetchState }) {
            const yearObject = getOrAddYearObject(this, value.year);
            yearObject.fetchState = value.fetchState;
        },

        setPayrollDatesForYear (
            value: { year: number, payrollDates: PayrollCalendarDate[] }) {
            const yearObject = getOrAddYearObject(this, value.year);
            const formattedPayrollDates = value.payrollDates.map(payrollDate => ({
                ...payrollDate,
                date: moment(payrollDate.date).format(DATE)
            }));

            yearObject.pristinePayrollDates = [ ...formattedPayrollDates ];
            yearObject.payrollDates = [ ...formattedPayrollDates ];
            yearObject.fetchState = FetchState.Loaded;
        },

        setEmployerInFocus (employerId: number) {
            if (this.employerId === employerId) {
                return;
            }

            this.byYear = [];
            this.employerId = employerId;
        },

        setDirtyFlag ({ year, isDirty }: { year: number, isDirty: boolean }) {
            const yearObject = this.byYear.find(o => o.year === year);
            if (!yearObject) {
                return;
            }

            yearObject.isDirty = isDirty;
        },

        applyDates ({ year, monthIndex, days }: { year: number, monthIndex: number, days: number[] }) {

            const yearObject = this.byYear.find(o => o.year === year);
            if (!yearObject) {
                return;
            }

            const monthStr = monthFromDate(moment().year(year).month(monthIndex).date(1).format(DATE));
            const requestedDates = days.map(day => moment().year(year).month(monthIndex).date(day).format(DATE));

            const payrollDatesThisMonthAndTheirIndex = yearObject.payrollDates
                .map((payroll, index) => {
                    if (monthFromDate(payroll.date) !== monthStr) {
                        return null;
                    }

                    return {
                        payroll,
                        index
                    };
                })
                .filter(payrollAndIndex => payrollAndIndex !== null);

            const indicesToRemove = payrollDatesThisMonthAndTheirIndex
                .filter(payrollAndIndex => !requestedDates.some(date => date === payrollAndIndex.payroll.date))
                .map(payrollAndIndex => payrollAndIndex.index);

            const payrollDatesThisMonth = payrollDatesThisMonthAndTheirIndex.map(obj => obj.payroll.date);
            const datesToAdd = requestedDates.filter(date => !payrollDatesThisMonth.some(existingDate => date === existingDate));

            if (indicesToRemove.length === 0 && datesToAdd.length === 0) {
                return;
            }

            yearObject.isDirty = true;
            indicesToRemove.sort(descendingOrder).forEach(index => yearObject.payrollDates.splice(index, 1));
            datesToAdd.forEach(date => yearObject.payrollDates.push({
                date,
                dateGenerationCode: DateGenerationCode.ManuallyAdjusted
            }));
        },

        setValidationFetchState (newState: FetchState): void {
            this.validationFetchState = newState;
        },

        setValidationMessages (validationMessages: PayrollValidationMessage[]) {
            this.validationMessages = validationMessages;
        },

        clearValidationMessages () {
            this.validationFetchState = FetchState.Uninitialized;
            this.validationMessages = [];
        },

        revertAllChanges () {
            this.byYear.forEach(yearObj => {
                yearObj.payrollDates = [ ...yearObj.pristinePayrollDates ];
                yearObj.isDirty = false;
            });
        },
        getPayrollDates ({ employerId, year }: { employerId: number, year: number }) {
            this.setEmployerInFocus(employerId);
            if (this.fetchStateForYear(year) !== FetchState.Uninitialized) {
                return Promise.resolve();
            }

            this.setFetchStateForYear({ year, fetchState: FetchState.Loading });

            return PayrollService.getPayrollDates({ employerId, year })
                .then(response => {
                    this.setPayrollDatesForYear({ year, payrollDates: response.data });
                })
                .catch(error => {
                    if (error.response && error.response.status === 404) {
                        this.setPayrollDatesForYear({ year, payrollDates: [] });
                        return;
                    }
                    return storeHelper.handleError({ error, rethrow: false });
                });
        },

        validatePayrollDates (year: number) {
            const yearObject = this.byYear.find(o => o.year === year);
            if (!yearObject || yearObject.fetchState !== FetchState.Loaded || !yearObject.isDirty) {
                return Promise.resolve();
            }

            this.setValidationFetchState(FetchState.Loading);

            const employerId = this.employerId;
            return PayrollService.validateEmployerPaydatesByYear({
                year,
                employerId,
                paydates: yearObject.payrollDates
            }).then(response => {
                if (employerId !== this.employerId) {
                    return; // because a different employerId was loaded since the POST was sent
                }

                this.setValidationMessages(response.data);
                this.setValidationFetchState(FetchState.Loaded);
            }).catch(error => {
                storeHelper.handleError({ error, rethrow: false });
            });
        },

        validateAllPayrollDates () {
            const employerId = this.employerId;
            this.clearValidationMessages();
            this.setValidationFetchState(FetchState.Loading);

            const currentYear = moment().year();

            const validationRequests = this.byYear
                .filter(y => y.year >= currentYear)
                .map(obj => ({
                    employerId,
                    year: obj.year,
                    paydates: obj.payrollDates
                }));

            const promises = validationRequests.map(req => PayrollService.validateEmployerPaydatesByYear(req));
            const allPromise = Promise.all(promises);
            allPromise
                .then(responses => {
                    if (employerId !== this.employerId) {
                        // TODO: does this need to reset the fetchState or clear the validationMessages?
                        return; // because a different employerId was loaded since the POSTs were sent
                    }

                    const messages = responses.reduce(
                        (msgs, response) => msgs.concat(response.data),
                        [] as PayrollValidationMessage[]
                    );

                    this.setValidationMessages(messages);
                    this.setValidationFetchState(FetchState.Loaded);
                })
                .catch(error => {
                    return storeHelper.handleError({ error, rethrow: false });
                });
        },

        savePayrollDates (args: { year: number, isEditMode: boolean }) {
            const yearObject = this.byYear.find(o => o.year === args.year);
            if (!yearObject || yearObject.fetchState !== FetchState.Loaded ||
                (!yearObject.isDirty && !args.isEditMode)) {
                return Promise.resolve();
            }

            const employerId = this.employerId;
            return PayrollService.saveEmployerPaydatesByYear({
                year: args.year,
                employerId,
                paydates: yearObject.payrollDates
            }).then(response => {
                if (employerId !== this.employerId) {
                    return; // because a different employerId was loaded since the PUT was sent
                }

                this.setPayrollDatesForYear({ year: args.year, payrollDates: response.data });
                this.setDirtyFlag({ year: args.year, isDirty: false });
            }).catch(error => {
                return storeHelper.handleError({ error, rethrow: false });
            });
        },

        saveAllPayrollDates (isEditMode = false) {
            const allYearsToBeSaved = this.byYear.filter(obj => obj.isDirty || isEditMode).map(obj => obj.year);
            const promises = allYearsToBeSaved.map(year => this.savePayrollDates({ year, isEditMode }));
            return Promise.all(promises);
        }
    }
});
